import React, { useEffect, useState } from 'react'
import ReactSelect from 'react-select'
import axios from 'axios'
import { toast } from 'react-toastify'
import Redux from 'app/interface/redux'
import Connect from 'app/redux/connect'
import Basic from 'app/redux/actions/pages/business/basic'
import { User } from '@marketing-milk/interfaces'
import BusinessAuthorizer from './businessAuthorizer'
import { NestAPI } from 'app/nestapi'
import { isFeatureFlagEnabled } from 'app/lib/posthog/isFeatureFlagEnabled'

interface GoogleAdsLeafAccount {
  id: string
  name: string
}

function formatAdsCustomerID(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 10)
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
}

function isCompleteAdsID(raw: string): boolean {
  return raw.replace(/\D/g, '').length === 10
}

const Google = (props: Redux) => {
  const { main } = props.pages.business
  const googleAuthEnabled = isFeatureFlagEnabled('google-auth')
  const [leafAccounts, setLeafAccounts] = useState<GoogleAdsLeafAccount[]>([])
  const [loadingLeafAccounts, setLoadingLeafAccounts] = useState(false)
  const [leafAccountsError, setLeafAccountsError] = useState<string | null>(null)

  // When an authorizer is selected and no manager ID is set yet, pre-fill with the default
  useEffect(() => {
    if (!main.google_authorizer_id || main.google_ads_manager_id) return
    NestAPI.get<{ managerId: string }>('businesses/manage/google/ads/default-manager-id')
      .then((res: any) => {
        const id = res?.managerId ?? res?.data?.managerId
        if (id) Basic.update('google_ads_manager_id', id)
      })
      .catch(() => {})
  }, [main.google_authorizer_id])

  // Fetch leaf accounts once both authorizer and a complete manager ID are set
  useEffect(() => {
    if (
      !main.google_authorizer_id ||
      !main.google_ads_manager_id ||
      !isCompleteAdsID(main.google_ads_manager_id)
    ) {
      setLeafAccounts([])
      setLeafAccountsError(null)
      return
    }
    setLoadingLeafAccounts(true)
    setLeafAccountsError(null)
    NestAPI.get<GoogleAdsLeafAccount[]>(
      `businesses/manage/google/ads/leaf-accounts?authorizerId=${main.google_authorizer_id}&managerId=${main.google_ads_manager_id}&businessId=${props.pages.business.id}`
    )
      .then((accounts: any) => setLeafAccounts(accounts ?? []))
      .catch(e => {
        const message = axios.isAxiosError(e)
          ? e.response?.data?.message ?? 'Failed to load Google Ads accounts.'
          : 'Failed to load Google Ads accounts.'
        toast.error(message)
        setLeafAccountsError(message)
        setLeafAccounts([])
      })
      .finally(() => setLoadingLeafAccounts(false))
  }, [main.google_authorizer_id, main.google_ads_manager_id, props.pages.business.id])

  return (
    <div className="m-form m-form--fit mb-5 mx-3">
      <div className="m-form__section">
        <div className="m-form__heading px-4 pt-3 pb-2">
          <h3 className="m-form__heading-title">Google</h3>
        </div>
        <div className="row px-4 pb-4">
          {googleAuthEnabled && (
            <div className="col-md-6 col-sm-12 col-xs-12">
              <BusinessAuthorizer
                title={'Google Authorizer'}
                business={props.pages.business}
                authorizerID={main.google_authorizer_id}
                assignAuthorizer={(authorizer: User) =>
                  Basic.update('google_authorizer_id', authorizer.id)
                }
                filterFn={u => u.googleConnected}
                popover={'This user must have their Google account linked under User Preferences'}
              />
            </div>
          )}
          <div className="col-md-6 col-sm-12 col-xs-12">
            <div className="d-flex flex-column align-items-start px-2 py-2">
              <span className="pb-2">Looker Embed URL</span>
              <input
                className="form-control m-input w-100"
                value={main.google_looker_embed_url || ''}
                onChange={e => Basic.update('google_looker_embed_url', e.target.value)}
              />
            </div>
          </div>
          {googleAuthEnabled && (
            <div className="col-md-6 col-sm-12 col-xs-12">
              <div className="d-flex flex-column align-items-start px-2 py-2">
                <span className="pb-2">Google Ads Manager Account ID</span>
                <input
                  className="form-control m-input w-100"
                  placeholder="000-000-0000"
                  value={formatAdsCustomerID(main.google_ads_manager_id || '')}
                  onChange={e =>
                    Basic.update('google_ads_manager_id', e.target.value.replace(/\D/g, ''))
                  }
                />
                <small className="text-muted mt-1">
                  Leave blank if the authorizer has direct access to the customer account
                </small>
              </div>
            </div>
          )}
          {googleAuthEnabled && (
            <div className="col-md-6 col-sm-12 col-xs-12">
              <div className="d-flex flex-column align-items-start px-2 py-2">
                <span className="pb-2">Google Ads Customer ID</span>
                {main.google_authorizer_id &&
                main.google_ads_manager_id &&
                isCompleteAdsID(main.google_ads_manager_id) ? (
                  <>
                    <ReactSelect
                      className="w-100"
                      isLoading={loadingLeafAccounts}
                      isClearable
                      options={leafAccounts}
                      value={leafAccounts.find(a => a.id === main.google_ads_customer_id) ?? null}
                      getOptionLabel={(x: GoogleAdsLeafAccount) =>
                        `${x.name} (${formatAdsCustomerID(x.id)})`
                      }
                      getOptionValue={(x: GoogleAdsLeafAccount) => x.id}
                      formatOptionLabel={(x: GoogleAdsLeafAccount) => (
                        <div>
                          <div>{x.name !== x.id ? x.name : formatAdsCustomerID(x.id)}</div>
                          {x.name !== x.id && (
                            <div style={{ fontSize: '0.85em', color: '#888' }}>
                              {formatAdsCustomerID(x.id)}
                            </div>
                          )}
                        </div>
                      )}
                      onChange={(option: any) =>
                        Basic.update('google_ads_customer_id', option?.id ?? null)
                      }
                    />
                    {leafAccountsError && (
                      <p className="text-danger mb-0 mt-1">{leafAccountsError}</p>
                    )}
                  </>
                ) : (
                  <input
                    className="form-control m-input w-100"
                    placeholder="000-000-0000"
                    value={formatAdsCustomerID(main.google_ads_customer_id || '')}
                    onChange={e =>
                      Basic.update('google_ads_customer_id', e.target.value.replace(/\D/g, ''))
                    }
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Connect(Google)
